import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { Lightbulb, Search } from "lucide-react";

const STATUSES = ["New", "Under Review", "Implemented", "Rejected"];
const STATUS_COLOR = {
  "New": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Under Review": "border-amber-200 bg-amber-50 text-amber-700",
  "Implemented": "border-indigo-200 bg-indigo-50 text-indigo-700",
  "Rejected": "border-slate-200 bg-slate-50 text-slate-500",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminSuggestions() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filters, setFilters] = useState({ status: "", q: "", date_from: "", date_to: "" });
  const [draft, setDraft] = useState({}); // id -> { status, internal_note }

  const load = async () => {
    try {
      const { data } = await api.get("/admin/suggestions", { params: filters });
      setRows(data.rows || []); setCounts(data.counts || {});
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const update = async (sid) => {
    const current = rows.find((r) => r.id === sid);
    const d = draft[sid] || {};
    const status = d.status || current.status;
    const internal_note = d.internal_note !== undefined ? d.internal_note : (current.internal_note || "");
    try {
      await api.patch(`/admin/suggestions/${sid}`, { status, internal_note });
      toast.success("Updated");
      setDraft((p) => ({ ...p, [sid]: undefined }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const setDraftField = (sid, k, v) => setDraft((p) => ({ ...p, [sid]: { ...(p[sid] || {}), [k]: v } }));
  const dirty = (sid, current) => {
    const d = draft[sid]; if (!d) return false;
    return (d.status && d.status !== current.status) ||
           (d.internal_note !== undefined && d.internal_note !== (current.internal_note || ""));
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar role="admin" />
      <main className="flex-1 bg-[#FCFCFC] p-6 lg:p-10">
        <div className="max-w-[1300px]">
          <div className="mb-6">
            <div className="overline text-emerald-700">Admin</div>
            <h1 className="font-editorial text-3xl mt-1 text-slate-900 flex items-center gap-3">
              <Lightbulb size={28} className="text-emerald-600" /> Agent Suggestions
            </h1>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {STATUSES.map((s) => (
              <button key={s} data-testid={`sugg-stat-${s}`}
                onClick={() => setFilters({ ...filters, status: filters.status === s ? "" : s })}
                className={`text-left border rounded p-4 transition ${filters.status === s ? "border-emerald-500 shadow-sm" : ""} ${STATUS_COLOR[s]}`}>
                <div className="text-[10px] uppercase tracking-widest opacity-80">{s}</div>
                <div className="font-editorial text-3xl mt-1 text-slate-900">{counts[s] ?? 0}</div>
              </button>
            ))}
          </div>

          <div className="bg-white border border-border rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search size={13} className="absolute left-3 top-3 text-slate-400" />
              <input data-testid="sugg-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Search text / agent / agency" className="w-full border border-border rounded pl-8 pr-3 py-2 text-sm" />
            </div>
            <select data-testid="sugg-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="border border-border rounded px-2 py-2 text-sm bg-white">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input data-testid="sugg-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="border border-border rounded px-2 py-2 text-sm" />
            <input data-testid="sugg-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="border border-border rounded px-2 py-2 text-sm" />
          </div>

          <div className="space-y-3">
            {rows.length === 0 && (
              <div className="text-sm text-slate-400 bg-white border border-dashed border-border rounded px-4 py-6 text-center">
                No suggestions match the current filters.
              </div>
            )}
            {rows.map((s) => {
              const d = draft[s.id] || {};
              const curStatus = d.status || s.status;
              const curNote = d.internal_note !== undefined ? d.internal_note : (s.internal_note || "");
              return (
                <div key={s.id} data-testid={`admin-sugg-${s.id}`} className="bg-white border border-border rounded p-5">
                  <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
                    <div>
                      <div className="font-semibold text-slate-900">{s.agency_name || s.agent_email}</div>
                      <div className="text-[11px] text-slate-500">{s.agent_email} · {fmt(s.created_at)}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border ${STATUS_COLOR[s.status] || STATUS_COLOR["New"]}`}>{s.status}</span>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 border border-border rounded p-3">{s.text}</div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
                    <select data-testid={`admin-sugg-status-${s.id}`} value={curStatus} onChange={(e) => setDraftField(s.id, "status", e.target.value)}
                      className="border border-border rounded px-2 py-2 text-sm bg-white">
                      {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                    <textarea data-testid={`admin-sugg-note-${s.id}`} value={curNote} onChange={(e) => setDraftField(s.id, "internal_note", e.target.value)}
                      rows={2} placeholder="Internal note / reply to agent" className="md:col-span-3 border border-border rounded px-3 py-2 text-sm" />
                    <button data-testid={`admin-sugg-save-${s.id}`} onClick={() => update(s.id)} disabled={!dirty(s.id, s)}
                      className="btn-bronze text-xs uppercase tracking-widest disabled:opacity-40">
                      Save
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
